import React, { useState, useEffect } from 'react';
import { Card, Radio, Spin, Empty, Typography, Space, Checkbox } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getScoreHistory } from '../api/trustgridApi';

const { Text } = Typography;

const ScoreTrendChart = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(30);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [xAxisMode, setXAxisMode] = useState('date');
  const [visibleScores, setVisibleScores] = useState(['trust', 'compliance', 'maturity']);

  useEffect(() => {
    if (projectId) {
      fetchHistory();
    }
  }, [projectId, timeRange, xAxisMode]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getScoreHistory(projectId, timeRange);
      const snapshots = response.data.snapshots || [];

      const chartData = snapshots.reverse().map((snapshot, index) => ({
        x: xAxisMode === 'date'
          ? new Date(snapshot.snapshot_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : `Scan ${index + 1}`,
        trustScore: snapshot.trust_score,
        complianceScore: snapshot.compliance_score,
        maturityScore: snapshot.maturity_score,
        timestamp: snapshot.snapshot_at
      }));

      setHistory(chartData);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No score history available');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch score history');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScoreToggle = (scoreType) => {
    setVisibleScores(prev => {
      if (prev.includes(scoreType)) {
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== scoreType);
      } else {
        return [...prev, scoreType];
      }
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          padding: '14px',
          background: 'white',
          border: '1px solid #e8e8e8',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{
            marginBottom: '10px',
            fontWeight: 600,
            color: '#262626',
            fontSize: '13px',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '8px'
          }}>
            {label}
          </div>
          {payload.map((entry, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              margin: '6px 0',
              justifyContent: 'space-between',
              minWidth: '220px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: entry.color,
                  marginRight: '10px'
                }} />
                <span style={{ color: '#333', fontSize: '13px' }}>{entry.name}</span>
              </div>
              <span style={{
                fontWeight: 700,
                color: '#333',
                marginLeft: '20px',
                fontSize: '14px'
              }}>
                {entry.value !== null && entry.value !== undefined ? entry.value.toFixed(1) : 'N/A'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card title="Score Trends">
        <Spin tip="Loading score history..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Score Trends">
        <Empty description={error} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card title="Score Trends">
        <Empty description="No score history available. Scores will appear here after scans are run." />
      </Card>
    );
  }

  const handleLegendClick = (dataKey) => {
    const scoreMap = {
      'Trust Score': 'trust',
      'Compliance Score': 'compliance',
      'Governance Maturity': 'maturity'
    };
    handleScoreToggle(scoreMap[dataKey]);
  };

  return (
    <Card
      title="Score Trends"
      extra={
        <Space>
          <Radio.Group value={xAxisMode} onChange={e => setXAxisMode(e.target.value)} size="small">
            <Radio.Button value="date">Timeline</Radio.Button>
            <Radio.Button value="scan">Sequential</Radio.Button>
          </Radio.Group>
          <Radio.Group value={timeRange} onChange={e => setTimeRange(e.target.value)} buttonStyle="solid">
            <Radio.Button value={7}>7 Days</Radio.Button>
            <Radio.Button value={30}>30 Days</Radio.Button>
            <Radio.Button value={90}>90 Days</Radio.Button>
            <Radio.Button value={365}>1 Year</Radio.Button>
          </Radio.Group>
        </Space>
      }
    >

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={history} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={{ fontSize: 12 }}
            label={{
              value: xAxisMode === 'date' ? 'Time' : 'Scan Number',
              position: 'insideBottom',
              offset: -5,
              style: { fontSize: 12 }
            }}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            align="right"
            wrapperStyle={{ paddingBottom: '10px', cursor: 'pointer' }}
            iconType="line"
            onClick={(e) => handleLegendClick(e.value)}
            formatter={(value) => {
              const scoreMap = {
                'Trust Score': 'trust',
                'Compliance Score': 'compliance',
                'Governance Maturity': 'maturity'
              };
              const isVisible = visibleScores.includes(scoreMap[value]);
              return (
                <span style={{
                  color: isVisible ? '#333' : '#ccc',
                  textDecoration: isVisible ? 'none' : 'line-through'
                }}>
                  {value}
                </span>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="trustScore"
            stroke="#1890ff"
            strokeWidth={3}
            strokeOpacity={visibleScores.includes('trust') ? 1 : 0}
            dot={visibleScores.includes('trust') ? { r: 6, fill: '#1890ff', strokeWidth: 2, stroke: '#fff' } : false}
            name="Trust Score"
            connectNulls
            hide={!visibleScores.includes('trust')}
          />
          <Line
            type="monotone"
            dataKey="complianceScore"
            stroke="#52c41a"
            strokeWidth={3}
            strokeDasharray="5 5"
            strokeOpacity={visibleScores.includes('compliance') ? 1 : 0}
            dot={visibleScores.includes('compliance') ? { r: 6, fill: '#52c41a', strokeWidth: 2, stroke: '#fff' } : false}
            name="Compliance Score"
            connectNulls
            hide={!visibleScores.includes('compliance')}
          />
          <Line
            type="monotone"
            dataKey="maturityScore"
            stroke="#fa8c16"
            strokeWidth={3}
            strokeDasharray="2 2"
            strokeOpacity={visibleScores.includes('maturity') ? 1 : 0}
            dot={visibleScores.includes('maturity') ? { r: 6, fill: '#fa8c16', strokeWidth: 2, stroke: '#fff' } : false}
            name="Governance Maturity"
            connectNulls
            hide={!visibleScores.includes('maturity')}
          />
        </LineChart>
      </ResponsiveContainer>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Text type="secondary">
          Showing last {timeRange} days of score history ({xAxisMode === 'date' ? 'timeline view' : 'sequential scans'})
        </Text>
      </div>
    </Card>
  );
};

export default ScoreTrendChart;
